import { Dispatch } from 'react';
import { _ } from '~/dependencies';
import api from '~/services';
import { log } from '~/helpers';
import {
  DepartmentList,
  FeatureFlags,
  UserListResponseData,
  UserList,
  SendersList,
  SmsTemplateList,
} from '~/types/accountInfo';
import { ChannelTypeEnum } from '~/enums/conversations';
import { RootState } from '../config';
import {
  SetDepartmentsAction,
  SetFeatureFlagsAction,
  SetUsersAction,
  SetSendersAction,
  SetSmsTemplatesAction,
} from '../reducers/accountInfo';
import * as actionTypes from './actionTypes';

const INVALID_RESPONSE_DATA_ERROR = 'Invalid response data.';

export const fetchDepartmentList = () => async (
  dispatch: Dispatch<SetDepartmentsAction>
) => {
  try {
    const departments: DepartmentList = (await api.accounts.get.departments())
      ?.data;

    if (!Array.isArray(departments))
      throw new Error(INVALID_RESPONSE_DATA_ERROR);

    dispatch({
      type: actionTypes.ACCOUNT_INFO_SET_DEPARTMENTS,
      payload: departments,
    });
  } catch (err) {
    log.error('Error to fetch account departments: ', err);
  }
};

export const fetchFeatureFlags = () => async (
  dispatch: Dispatch<SetFeatureFlagsAction>
) => {
  try {
    const featureFlags: string[] = (await api.accounts.get.featureFlags())
      ?.data;

    if (!Array.isArray(featureFlags))
      throw new Error(INVALID_RESPONSE_DATA_ERROR);

    const parsedFeatureFlags: FeatureFlags = {
      isWhatsappEnabled: featureFlags.includes('whatsapp_flag'),
      isMmsEnabled: featureFlags.includes('mms_feature'),
      isMicrophoneWarningEnabled: featureFlags.includes(
        'desktop_microphone_warning'
      ),
      isYealinkCallControlEnabled: featureFlags.includes(
        'desktop_yealink_call_control'
      ),
    };

    dispatch({
      type: actionTypes.ACCOUNT_INFO_SET_FEATURE_FLAGS,
      payload: parsedFeatureFlags,
    });
  } catch (err) {
    log.error('Error to fetch account feature flags: ', err);
  }
};

export const fetchUserList = () => async (
  dispatch: Dispatch<SetUsersAction>
) => {
  try {
    const userListData: UserListResponseData = (
      await api.accounts.get.getUserList()
    )?.data;

    if (!Array.isArray(userListData))
      throw new Error(INVALID_RESPONSE_DATA_ERROR);

    // The app was expecting already in a given format.
    const userList: UserList = userListData.map(user => ({
      id: Number(user.userId),
      name: user.userName,
      email: user.userEmail,
      isAdmin: user.accountAdmin,
      isActive: Boolean(user.userActive),
    }));

    dispatch({
      type: actionTypes.ACCOUNT_INFO_SET_USER_LIST,
      payload: [...userList],
    });
  } catch (err) {
    log.error('Error to fetch user list: ', err);
  }
};

/**
 * Fetch SMS senders associated with the account.
 * Sender can have two types:
 * 1. Alphanumeric (known as senderName): E.g, "NUACOM"
 * 2. Numeric (known as senderId): E.g, "+441632960961" which is a valid phone number in international format.
 */
export const fetchMessageSenders = () => async (
  dispatch: Dispatch<SetSendersAction>,
  getState: () => RootState
) => {
  try {
    const {
      filters: { channelType },
    } = getState().conversation;

    // Fetch senders based on selected channel type.
    const rawSenders: SendersList =
      channelType === ChannelTypeEnum.WHATSAPP
        ? (await api.accounts.get.whatsappSenders())?.data
        : (await api.accounts.get.smsSenders())?.data;

    if (!Array.isArray(rawSenders))
      throw new Error(INVALID_RESPONSE_DATA_ERROR);

    const senderList = rawSenders.map(sender => ({
      ...sender,
      name: sender.senderName,
      isDefault: sender.isDefault ?? false,
    }));

    dispatch({
      type: actionTypes.ACCOUNT_INFO_SET_SENDERS,
      payload: [...senderList],
    });
  } catch (err) {
    log.error('Error to fetch message senders: ', err);
  }
};

export const fetchSmsTemplates = (page: number, perPage: number) => async (
  dispatch: Dispatch<SetSmsTemplatesAction>,
  getState: () => RootState
) => {
  try {
    const { smsTemplates } = getState().accountInfo;

    const smsTemplatesData: SmsTemplateList = (
      await api.accounts.get.getTemplates(page, perPage)
    ).data.data;

    if (!Array.isArray(smsTemplatesData))
      throw new Error(INVALID_RESPONSE_DATA_ERROR);

    const uniqueSmsTemplates: SmsTemplateList = _.uniqBy(
      [...smsTemplates, ...smsTemplatesData],
      'id'
    );

    // Merge local state with response data and remove duplicates
    dispatch({
      type: actionTypes.ACCOUNT_INFO_SET_SMS_TEMPLATE,
      payload: uniqueSmsTemplates,
    });
  } catch (err) {
    log.error('Error to fetch sms templates: ', err);
  }
};
